import React, { useEffect, useRef, useState } from "react";
import { X, UserRound, Mail, Phone, Shield, Power, Crown, Info, LockKeyhole, Save, ChevronDown } from "lucide-react";
import { updateAdminProfile } from "../adminAccountsApi";
import { getFriendlyErrorMessage } from "../../../app/apiClient";
import "./EditAdminProfileDialog.css";

export default function EditAdminProfileDialog({ admin, onClose, onSaved }) {
  const dialog = useRef(null);
  const submitting = useRef(false);
  const [form, setForm] = useState({ fullname: admin.name, email: admin.email, phoneNumber: admin.phone, role: admin.role || "", isEnable: admin.enabled });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const trigger = document.activeElement;
    const element = dialog.current;
    element.showModal();
    return () => { element.close(); if (trigger?.isConnected) trigger.focus(); };
  }, []);

  const close = () => { if (!submitting.current) onClose(); };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    const profile = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value]));
    if ([profile.fullname, profile.email, profile.phoneNumber, profile.role].some((value) => !value)) { setError("Complete all required fields."); return; }
    if ((profile.fullname.match(/\p{L}/gu) || []).length < 4) { setError("Full name must contain at least 4 letters."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) { setError("Enter a valid email address."); return; }
    if (!/^[0-9]{10,}$/.test(profile.phoneNumber)) { setError("Phone number must contain at least 10 digits, using numbers only."); return; }
    submitting.current = true;
    setSaving(true);
    setError("");
    try {
      await updateAdminProfile(admin.id, profile);
      onSaved("Admin profile updated successfully.");
    } catch (err) {
      setError(err.response?.status === 409
        ? err.response.data?.message || "This update conflicts with an existing account or an account access rule."
        : getFriendlyErrorMessage(err, "We could not update this profile. Please try again."));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return <dialog ref={dialog} className="cm-dialog cm-modal admin-profile-dialog" aria-labelledby="edit-admin-title" aria-describedby="edit-admin-description" onCancel={(event) => { event.preventDefault(); close(); }}>
    <form onSubmit={submit} noValidate aria-label="Edit admin profile" aria-busy={saving}>
      <header className="admin-profile-header">
        <div className="admin-profile-eyebrow">Admin Profile</div>
        <h2 id="edit-admin-title">Edit Profile</h2>
        <p id="edit-admin-description">Update contact details, role and access for {admin.name}.</p>
        <button type="button" className="admin-profile-close" aria-label="Close dialog" disabled={saving} onClick={close}><X aria-hidden="true" /></button>
      </header>
      <fieldset className="admin-profile-body" disabled={saving}>
        {[["fullname", "Full Name", "text", "name", UserRound, "Enter full name"], ["email", "Email Address", "email", "email", Mail, "Enter email address"], ["phoneNumber", "Phone Number", "tel", "tel", Phone, "Enter phone number"]].map(([name, label, type, autoComplete, Icon, placeholder]) => <div className="admin-profile-field" key={name}>
          <label htmlFor={`edit-admin-${name}`}><span className="admin-profile-icon"><Icon aria-hidden="true" /></span><span>{label} <span className="admin-profile-required" aria-hidden="true">*</span></span></label>
          <input id={`edit-admin-${name}`} name={name} type={type} autoComplete={autoComplete} placeholder={placeholder} required inputMode={name === "phoneNumber" ? "numeric" : undefined} value={form[name]} onChange={(event) => setForm({ ...form, [name]: event.target.value })} />
        </div>)}
        <div className="admin-profile-access">
          <div className="admin-profile-field">
            <label htmlFor="edit-admin-role"><span className="admin-profile-icon"><Shield aria-hidden="true" /></span><span>Role <span className="admin-profile-required" aria-hidden="true">*</span></span></label>
            <div className="admin-profile-select admin-profile-role">
              <span className="admin-profile-select-icon"><Crown aria-hidden="true" /></span>
              <select id="edit-admin-role" name="role" required aria-describedby="edit-admin-role-help" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
                <option value="" disabled>Select a role</option><option value="ADMIN">ADMIN</option><option value="SUPERADMIN">SUPERADMIN</option>
              </select>
              <ChevronDown className="admin-profile-chevron" aria-hidden="true" />
            </div>
            <div className="admin-profile-hint" id="edit-admin-role-help"><Info aria-hidden="true" /><p>Superadmins can manage administrator accounts and access.</p></div>
          </div>
          <div className="admin-profile-field">
            <label htmlFor="edit-admin-status"><span className="admin-profile-icon admin-profile-green"><Power aria-hidden="true" /></span><span>Account Status <span className="admin-profile-required" aria-hidden="true">*</span></span></label>
            <div className="admin-profile-select">
              <span className={`admin-profile-status-dot ${form.isEnable ? "" : "is-disabled"}`} aria-hidden="true" />
              <select id="edit-admin-status" name="isEnable" required aria-describedby="edit-admin-status-help" value={String(form.isEnable)} onChange={(event) => setForm({ ...form, isEnable: event.target.value === "true" })}>
                <option value="true">Enabled</option><option value="false">Disabled</option>
              </select>
              <ChevronDown className="admin-profile-chevron" aria-hidden="true" />
            </div>
            <div className="admin-profile-hint admin-profile-hint-green" id="edit-admin-status-help"><Info aria-hidden="true" /><p>Disabled administrators cannot sign in or access the portal.</p></div>
          </div>
        </div>
        <div className="admin-profile-signin"><span><LockKeyhole aria-hidden="true" /></span><p>Use the updated email address for future sign-ins.</p></div>
        {error && <p className="admin-profile-error" role="alert">{error}</p>}
      </fieldset>
      <footer className="admin-profile-footer">
        <button type="button" className="admin-profile-cancel" disabled={saving} onClick={close}>Cancel</button>
        <button type="submit" className="admin-profile-save" disabled={saving}><Save aria-hidden="true" />{saving ? "Saving..." : "Save Changes"}</button>
      </footer>
    </form>
  </dialog>;
}
